import { ApiError } from './types'
import type {
  DownloadSasResponse,
  DropListResponse,
  DropView,
  FinalizeDropResponse,
  MeResponse,
  MfaConfirmResponse,
  MfaRecoverStartResponse,
  MfaSetupResponse,
  OtpSendResponse,
  OtpVerifyResponse,
  PrepareObjectSpec,
  PrepareObjectsResponse,
  RecipientObjectListResponse,
  RecipientSessionResponse,
  RegisterResponse,
} from './types'

const CSRF_HEADER = 'X-CSRF-Token'

let creatorCsrf: string | null = null
let recipientCsrf: string | null = null

export function getCreatorCsrf(): string | null {
  return creatorCsrf
}

export function setCreatorCsrf(token: string | null): void {
  creatorCsrf = token
}

export function getRecipientCsrf(): string | null {
  return recipientCsrf
}

export function setRecipientCsrf(token: string | null): void {
  recipientCsrf = token
}

async function parseDetail(res: Response): Promise<string> {
  try {
    const data: unknown = await res.json()
    if (data && typeof data === 'object' && 'detail' in data) {
      const detail = (data as { detail: unknown }).detail
      if (typeof detail === 'string') return detail
      return JSON.stringify(detail)
    }
    return res.statusText || 'Request failed'
  } catch {
    return res.statusText || 'Request failed'
  }
}

function retryAfter(res: Response): number | null {
  const raw = res.headers.get('Retry-After')
  if (!raw) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  options: { csrf?: 'creator' | 'recipient' | 'none' } = {},
): Promise<T> {
  const headers = new Headers(init.headers)
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  const csrfMode = options.csrf ?? 'none'
  if (csrfMode === 'creator') {
    if (!creatorCsrf) throw new ApiError(403, 'Missing creator CSRF token')
    headers.set(CSRF_HEADER, creatorCsrf)
  } else if (csrfMode === 'recipient') {
    if (!recipientCsrf) throw new ApiError(403, 'Missing recipient CSRF token')
    headers.set(CSRF_HEADER, recipientCsrf)
  }

  const res = await fetch(path, {
    ...init,
    headers,
    credentials: 'include',
  })

  if (!res.ok) {
    throw new ApiError(res.status, await parseDetail(res), retryAfter(res))
  }

  if (res.status === 204) {
    return undefined as T
  }

  return (await res.json()) as T
}

export const api = {
  register(email: string, password: string): Promise<RegisterResponse> {
    return request('/api/v1/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
  },

  verifyEmail(token: string): Promise<void> {
    return request('/api/v1/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    })
  },

  forgotPassword(email: string): Promise<void> {
    return request('/api/v1/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  },

  resetPassword(token: string, password: string): Promise<void> {
    return request('/api/v1/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    })
  },

  login(email: string, password: string): Promise<void> {
    return request('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
  },

  async me(): Promise<MeResponse> {
    const me = await request<MeResponse>('/api/v1/auth/me')
    setCreatorCsrf(me.csrf_token)
    return me
  },

  async logout(): Promise<void> {
    try {
      await request('/api/v1/auth/logout', { method: 'POST' }, { csrf: 'creator' })
    } finally {
      setCreatorCsrf(null)
    }
  },

  mfaSetup(): Promise<MfaSetupResponse> {
    return request('/api/v1/auth/mfa/setup', { method: 'POST' }, { csrf: 'creator' })
  },

  mfaConfirm(totpCode: string): Promise<MfaConfirmResponse> {
    return request(
      '/api/v1/auth/mfa/confirm',
      { method: 'POST', body: JSON.stringify({ totp_code: totpCode }) },
      { csrf: 'creator' },
    )
  },

  mfaRecoverStart(email: string, recoveryCode: string): Promise<MfaRecoverStartResponse> {
    return request('/api/v1/auth/mfa/recover/start', {
      method: 'POST',
      body: JSON.stringify({ email, recovery_code: recoveryCode }),
    })
  },

  mfaRecoverConfirm(recoveryToken: string, totpCode: string): Promise<MfaConfirmResponse> {
    return request('/api/v1/auth/mfa/recover/confirm', {
      method: 'POST',
      body: JSON.stringify({ recovery_token: recoveryToken, totp_code: totpCode }),
    })
  },

  listDrops(): Promise<DropListResponse> {
    return request('/api/v1/drops')
  },

  getDrop(dropId: string): Promise<DropView> {
    return request(`/api/v1/drops/${encodeURIComponent(dropId)}`)
  },

  createDrop(recipientPhone: string, ttlHours?: number): Promise<DropView> {
    return request(
      '/api/v1/drops',
      {
        method: 'POST',
        body: JSON.stringify({
          recipient_phone: recipientPhone,
          ...(ttlHours !== undefined ? { ttl_hours: ttlHours } : {}),
        }),
      },
      { csrf: 'creator' },
    )
  },

  prepareObjects(dropId: string, objects: PrepareObjectSpec[]): Promise<PrepareObjectsResponse> {
    return request(
      `/api/v1/drops/${encodeURIComponent(dropId)}/objects/prepare`,
      { method: 'POST', body: JSON.stringify({ objects }) },
      { csrf: 'creator' },
    )
  },

  finalizeDrop(dropId: string): Promise<FinalizeDropResponse> {
    return request(
      `/api/v1/drops/${encodeURIComponent(dropId)}/finalize`,
      { method: 'POST' },
      { csrf: 'creator' },
    )
  },

  revokeDrop(dropId: string): Promise<DropView> {
    return request(
      `/api/v1/drops/${encodeURIComponent(dropId)}/revoke`,
      { method: 'POST' },
      { csrf: 'creator' },
    )
  },

  otpSend(dropId: string): Promise<OtpSendResponse> {
    return request(`/api/v1/d/${encodeURIComponent(dropId)}/otp/send`, { method: 'POST' })
  },

  async otpVerify(dropId: string, code: string): Promise<OtpVerifyResponse> {
    const body = await request<OtpVerifyResponse>(
      `/api/v1/d/${encodeURIComponent(dropId)}/otp/verify`,
      { method: 'POST', body: JSON.stringify({ code }) },
    )
    setRecipientCsrf(body.csrf_token)
    return body
  },

  async recipientSession(dropId: string): Promise<RecipientSessionResponse> {
    const body = await request<RecipientSessionResponse>(
      `/api/v1/d/${encodeURIComponent(dropId)}/session`,
    )
    setRecipientCsrf(body.csrf_token)
    return body
  },

  recipientObjects(dropId: string): Promise<RecipientObjectListResponse> {
    return request(`/api/v1/d/${encodeURIComponent(dropId)}/objects`)
  },

  downloadObjectSas(dropId: string, objectId: string): Promise<DownloadSasResponse> {
    return request(
      `/api/v1/d/${encodeURIComponent(dropId)}/objects/${encodeURIComponent(objectId)}/download-sas`,
      { method: 'POST' },
      { csrf: 'recipient' },
    )
  },

  downloadZipSas(dropId: string): Promise<DownloadSasResponse> {
    return request(
      `/api/v1/d/${encodeURIComponent(dropId)}/download-zip-sas`,
      { method: 'POST' },
      { csrf: 'recipient' },
    )
  },
}

/** PUT file bytes to a short-lived write SAS URL (no cookies). */
export async function putWithWriteSas(
  writeSasUrl: string,
  data: Blob | ArrayBuffer,
  contentType: string,
): Promise<void> {
  const res = await fetch(writeSasUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType,
      'x-ms-blob-type': 'BlockBlob',
    },
    body: data,
  })
  if (!res.ok) {
    throw new ApiError(res.status, `Upload failed (${res.status})`)
  }
}
